import { strapiClient } from '../core/api/strapi-client';
import { regionStore } from '../store/jotai/store';
import {
  conversationsAtom,
  activeConversationAtom,
  messagesAtom,
  totalUnreadAtom,
  messagesPaginationAtom,
  messagesLoadingAtom
} from '../store/jotai/atoms/conversations.atom';
import { StrapiConversation } from '../core/models/strapi-conversation.model';

const getConversations = async (): Promise<void> => {
  const response = await strapiClient.getConversations();
  const { asUser, asVendor } = response.data;

  regionStore.set(conversationsAtom, { asUser, asVendor });

  const totalUnread =
    asUser.reduce((sum, c) => sum + (c.userUnreadCount ?? 0), 0) +
    asVendor.reduce((sum, c) => sum + (c.vendorUnreadCount ?? 0), 0);

  regionStore.set(totalUnreadAtom, totalUnread);
};

const getOrCreateConversation = async (vendorId: number): Promise<StrapiConversation> => {
  const response = await strapiClient.getOrCreateConversation(vendorId);
  const conversation = response.data;

  regionStore.set(activeConversationAtom, conversation);

  return conversation;
};

const PAGE_SIZE = 20;

const getMessages = async (conversationId: number): Promise<void> => {
  regionStore.set(messagesAtom, []);
  regionStore.set(messagesPaginationAtom, { page: 1, hasMore: false });
  regionStore.set(messagesLoadingAtom, true);
  try {
    const response = await strapiClient.getChatMessages(conversationId, 1, PAGE_SIZE);
    regionStore.set(messagesAtom, response.data);
    regionStore.set(messagesPaginationAtom, {
      page: 1,
      hasMore: response.meta.total > PAGE_SIZE
    });
  } finally {
    regionStore.set(messagesLoadingAtom, false);
  }
};

const loadMoreMessages = async (conversationId: number): Promise<void> => {
  const { page } = regionStore.get(messagesPaginationAtom);
  const nextPage = page + 1;
  const response = await strapiClient.getChatMessages(conversationId, nextPage, PAGE_SIZE);
  const current = regionStore.get(messagesAtom);
  regionStore.set(messagesAtom, [...response.data, ...current]);
  regionStore.set(messagesPaginationAtom, {
    page: nextPage,
    hasMore: response.meta.total > nextPage * PAGE_SIZE
  });
};

const sendMessage = async (conversationId: number, content: string): Promise<void> => {
  await strapiClient.sendChatMessage(conversationId, content);
  // The socket event will append the message; optimistic update not needed
};

const markAsRead = async (conversationId: number): Promise<void> => {
  await strapiClient.markConversationRead(conversationId);

  const conversations = regionStore.get(conversationsAtom);

  // Reset unread counts for this conversation on both sides
  regionStore.set(conversationsAtom, {
    asUser: conversations.asUser.map((c) =>
      c.id === conversationId ? { ...c, userUnreadCount: 0 } : c
    ),
    asVendor: conversations.asVendor.map((c) =>
      c.id === conversationId ? { ...c, vendorUnreadCount: 0 } : c
    )
  });

  const updated = regionStore.get(conversationsAtom);
  const totalUnread =
    updated.asUser.reduce((sum, c) => sum + (c.userUnreadCount ?? 0), 0) +
    updated.asVendor.reduce((sum, c) => sum + (c.vendorUnreadCount ?? 0), 0);

  regionStore.set(totalUnreadAtom, totalUnread);
};

export const chatService = {
  getConversations,
  getOrCreateConversation,
  getMessages,
  loadMoreMessages,
  sendMessage,
  markAsRead
};
